import { FirstPage, LastPage } from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardRounded';
import { Box, Button, Chip, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { getAccessToken } from 'src/routes/AuthContext';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { updateDocumentStatusesAction } from 'src/redux/actions/physicianActions';
import physicianService, {
  PhysicianDocument,
  compositeWorkflowStatusFromDocument,
} from 'src/services/apiServices/physician-service/physician-service';
import { useNavigate } from 'react-router';
import SignatureModal from './components/SignatureModal';
import { singleSignatureWorkflow } from 'src/config';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';

interface LocationState {
  selectedDocuments: PhysicianDocument[];
  source?: 'PHYSICIAN' | string;
}

interface RootState {
  getTherapistProfileReducer: {
    response: {
      data: {
        providerId: string;
      };
    };
  };
}

const PhysicianDocumentsSign: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isSingleSignatureWorkflow] = useFeatureToggleWhenReady(singleSignatureWorkflow, 'ENTERPRISE')

  const { state } = useLocation() as { state: LocationState };

  const [currentDocIndex, setCurrentDocIndex] = useState(0);
  const [iframeRef, setIframeRef] = useState<HTMLIFrameElement | null>(null);
  const [approvedDocs, setApprovedDocs] = useState<Set<string>>(new Set());
  const [isSignModalOpen, setIsSignModalOpen] = useState(false);
  const [signatureName, setSignatureName] = useState('');

  const user = useSelector((state: RootState) => state.getTherapistProfileReducer?.response?.data);

  const selectedDocuments = useMemo(
    () => state?.selectedDocuments ?? [],
    [state?.selectedDocuments]
  );
  const currentDocument = useMemo(
    () => selectedDocuments[currentDocIndex],
    [selectedDocuments, currentDocIndex]
  );
  const isCurrentDocumentApproved = useMemo(
    () => currentDocument && approvedDocs.has(currentDocument.uuid),
    [currentDocument, approvedDocs]
  );

  const updateIFrameRef = useCallback((node: HTMLIFrameElement) => {
    setIframeRef(node);
  }, []);

  const handleNavigate = useCallback((index: number) => {
    setCurrentDocIndex(index);
  }, []);

  const handleApproveDocument = useCallback(async () => {
    if (!currentDocument || !user?.providerId) return;

    const documentsToUpdate = [currentDocument.uuid];
    dispatch(
      updateDocumentStatusesAction(user.providerId, documentsToUpdate, 'APPROVED', user.providerId)
    );

    setApprovedDocs((prev) => new Set([...prev, currentDocument.uuid]));

    if (currentDocIndex < selectedDocuments.length - 1) {
      setCurrentDocIndex((prev) => prev + 1);
    }
  }, [currentDocument, user?.providerId, currentDocIndex, selectedDocuments.length, dispatch]);

  const handleSignApprovedDocuments = useCallback(() => {
    if (!user?.providerId || approvedDocs.size === 0) return;
    setIsSignModalOpen(true);
  }, [user?.providerId, approvedDocs]);


  const signDocumentsInBulk = async (payloads: any[]) => {
    try {
      const results = await Promise.all(
        payloads.map((payload) => physicianService.signPhysicianDocument(payload))
      );

      // Filter only successfully signed documents
      const successfullySignedDocuments = results
        .map((result: any, index: any) => (result.status === 200 ? payloads[index] : null))
        .filter((doc): doc is { formSubmissionUuid: string } => doc !== null)
        .map(doc => doc.formSubmissionUuid);

      if (successfullySignedDocuments.length > 0) {
        dispatch(
          updateDocumentStatusesAction(
            user.providerId,
            successfullySignedDocuments,
            "SIGNED",
            signatureName
          )
        );
      }
    } catch (error) {
      console.error("Error signing documents:", error);
    }
  };


  const handleSignConfirm = useCallback(
    async (signatureName: string) => {
      if (!user?.providerId) return;

      try {
        const documentsToUpdate = Array.from(approvedDocs);

        if (isSingleSignatureWorkflow) {
          const payload = documentsToUpdate?.map((uuid: any) => {
            return {
              formSubmissionUuid: uuid,
              signerName: signatureName,
              consentConfirmation: true,
              isSupervisor: true
            }
          })
          signDocumentsInBulk(payload)
        } else {
          dispatch(
            updateDocumentStatusesAction(user.providerId, documentsToUpdate, 'SIGNED', signatureName)
          );
        }

        setIsSignModalOpen(false);
        navigate('/physician-dashboard', {
          state: {
            signedDocumentIds: documentsToUpdate,
          },
        });
      } catch (error) {
        console.error('Error signing documents:', error);
      }
    },
    [user?.providerId, approvedDocs, dispatch, navigate]
  );

  useEffect(() => {
    if (!iframeRef) return;

    const interval = setInterval(async () => {
      await getAccessToken();
    }, 15000);

    return () => clearInterval(interval);
  }, [iframeRef]);

  useEffect(() => {
    if (selectedDocuments.length > 0) {
      console.log(selectedDocuments);
      const initialApprovedDocs = selectedDocuments
        .filter((doc) => compositeWorkflowStatusFromDocument(doc) === 'APPROVED')
        .map((doc) => doc.uuid);
      setApprovedDocs(new Set(initialApprovedDocs));
    }
  }, [selectedDocuments]);

  const NavigationButtons = () => (
    <Stack
      direction="row"
      spacing={1}
      sx={{ ml: 2 }}
    >
      <Button
        size="small"
        disabled={currentDocIndex === 0}
        onClick={() => handleNavigate(0)}
      >
        <FirstPage />
      </Button>
      <Button
        size="small"
        disabled={currentDocIndex === 0}
        onClick={() => handleNavigate(currentDocIndex - 1)}
      >
        <ArrowBackIcon />
      </Button>
      <Button
        variant="contained"
        color="primary"
        sx={{ px: 2 }}
        onClick={handleApproveDocument}
        disabled={isCurrentDocumentApproved}
      >
        {isCurrentDocumentApproved ? 'Document Approved' : 'Approve Document'}
      </Button>
      <Button
        size="small"
        disabled={currentDocIndex === selectedDocuments.length - 1}
        onClick={() => handleNavigate(currentDocIndex + 1)}
      >
        <ArrowForwardIcon />
      </Button>
      <Button
        size="small"
        disabled={currentDocIndex === selectedDocuments.length - 1}
        onClick={() => handleNavigate(selectedDocuments.length - 1)}
      >
        <LastPage />
      </Button>
    </Stack>
  );

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        <Box
          sx={{
            position: 'relative',
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pl: 2 }}>
            <Typography variant="h5">Approval Pending</Typography>
            <Chip
              label={`for ${selectedDocuments.length - approvedDocs.size} / ${
                selectedDocuments.length
              } Documents`}
              color="primary"
              variant="outlined"
            />
            {selectedDocuments.length > -1 && <NavigationButtons />}
          </Box>
          <Box sx={{ pr: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSignApprovedDocuments}
              disabled={approvedDocs.size === 0}
            >
              Sign Approved Documents
            </Button>
          </Box>
        </Box>

        <Link
          style={{
            color: '#111927',
            textDecoration: 'none',
            marginLeft: '1rem',
            marginBottom: '1rem',
            display: 'block',
          }}
          to={`${state?.source === 'PHYSICIAN' ? '/physician-dashboard' : '/dashboard'}`}
        >
          <Button
            sx={{
              color: '#111927',
              padding: '4px 8px',
              minWidth: 'unset',
            }}
            startIcon={<ArrowBackIcon sx={{ color: '#6C737F' }} />}
          >
            <Typography variant="body2">Back to Dashboard</Typography>
          </Button>
        </Link>
      </Box>

      <Box
        sx={{ height: 'calc(100% - 120px)' }}
        p={3}
      >
        <iframe
          src={currentDocument?.documentUrl || ''}
          width="100%"
          ref={updateIFrameRef}
          height="100%"
          style={{ border: 'none' }}
          title={`Preview document - ${currentDocument?.documentType}`}
        />
      </Box>
      <SignatureModal
        open={isSignModalOpen}
        onClose={() => setIsSignModalOpen(false)}
        onSign={handleSignConfirm}
        signatureName={signatureName}
        setSignatureName={setSignatureName}
      />
    </>
  );
};

export default PhysicianDocumentsSign;
